import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MILITARY_BRANCHES = ["Army", "Navy", "Air Force", "Marines", "Coast Guard", "Space Force", "National Guard"];

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatMonthYear = (value, prev) => {
  let v = value.replace(/[^\d/]/g, "");
  if (v.length === 2 && !v.includes("/") && (prev || "").length < v.length) v = v + "/";
  return v.slice(0, 7);
};

const SectionCard = ({ children }) => (
  <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 space-y-3">{children}</div>
);

const Wrapper = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-8">
    <div className="w-full max-w-2xl mx-auto space-y-4">
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 text-center">
        <img src="/brothers-logo.png" alt="Brothers of the Highway" className="w-32 mx-auto mb-3" />
        <h1 className="text-2xl font-bold text-white">B.O.H. Member Profile Questionnaire</h1>
      </div>
      {children}
    </div>
  </div>
);

const YesNo = ({ value, onChange, testId }) => (
  <div className="flex gap-3" data-testid={testId}>
    {[
      { label: "Yes", val: true },
      { label: "No", val: false },
    ].map((opt) => (
      <button
        key={opt.label}
        type="button"
        data-testid={`${testId}-${opt.label.toLowerCase()}`}
        onClick={() => onChange(opt.val)}
        className={`px-6 py-2 rounded-full border text-sm font-medium transition-colors ${
          value === opt.val
            ? "bg-blue-600 border-blue-500 text-white"
            : "bg-slate-900 border-slate-600 text-slate-300 hover:border-slate-400"
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

export default function MemberQuestionnaire() {
  const { token } = useParams();
  const [pageState, setPageState] = useState("loading"); // loading | form | invalid | submitted
  const [errorDetail, setErrorDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [prefill, setPrefill] = useState({});
  const [reason, setReason] = useState("promotion");
  const [form, setForm] = useState({
    name: "",
    personal_email: "",
    phone: "",
    address: "",
    dob: "",
    experience_start: "",
    military_service: null,
    military_branch: "",
    is_first_responder: null,
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relationship: "",
    emergency_contact_private: false,
    directory_consent: "",
  });

  useEffect(() => {
    axios
      .get(`${API}/questionnaire/${token}`)
      .then((res) => {
        const p = res.data.prefill || {};
        setPrefill(p);
        setReason(res.data.reason || "promotion");
        setForm((f) => ({
          ...f,
          name: p.name || "",
          personal_email: p.personal_email || "",
          phone: p.phone ? formatPhone(p.phone) : "",
          address: p.address || "",
          dob: p.dob || "",
          experience_start: p.experience_start || "",
        }));
        setPageState("form");
      })
      .catch((err) => {
        setErrorDetail(err.response?.data?.detail || "This questionnaire link is invalid.");
        setPageState("invalid");
      });
  }, [token]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.personal_email.trim() || !form.phone.trim() || !form.address.trim() || !form.dob) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!/^\d{2}\/\d{4}$/.test(form.experience_start)) {
      toast.error("Please enter your Trucking Experience Start as MM/YYYY");
      return;
    }
    if (form.military_service === null || form.is_first_responder === null) {
      toast.error("Please answer the Military Veteran and Police/Fire/EMS questions");
      return;
    }
    if (form.military_service && !form.military_branch) {
      toast.error("Please select your branch of service");
      return;
    }
    if (!form.directory_consent) {
      toast.error("Please answer the directory consent question (§6.8.3)");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/questionnaire/${token}/submit`, {
        ...form,
        military_service: !!form.military_service,
        is_first_responder: !!form.is_first_responder,
      });
      setPageState("submitted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit questionnaire");
    } finally {
      setSubmitting(false);
    }
  };

  if (pageState === "loading") {
    return (
      <Wrapper>
        <SectionCard>
          <div className="flex items-center justify-center gap-2 text-slate-300 py-8" data-testid="questionnaire-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading questionnaire…
          </div>
        </SectionCard>
      </Wrapper>
    );
  }

  if (pageState === "invalid") {
    return (
      <Wrapper>
        <SectionCard>
          <div className="text-center py-8 space-y-3" data-testid="questionnaire-invalid">
            <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto" />
            <p className="text-white font-semibold">Link Unavailable</p>
            <p className="text-slate-300 text-sm">{errorDetail}</p>
            <p className="text-slate-500 text-xs">If you need a new link, contact an officer of your chapter.</p>
          </div>
        </SectionCard>
      </Wrapper>
    );
  }

  if (pageState === "submitted") {
    return (
      <Wrapper>
        <SectionCard>
          <div className="text-center py-8 space-y-3" data-testid="questionnaire-success">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto" />
            <p className="text-white font-semibold text-lg">{reason === "update" ? "Thank You!" : "Welcome, Brother!"}</p>
            <p className="text-slate-300 text-sm">
              Your member profile has been updated. Thank you for completing the questionnaire.
            </p>
          </div>
        </SectionCard>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <SectionCard>
        <p className="text-slate-300 text-sm leading-relaxed">
          The information provided to Brothers of the Highway is considered <span className="font-semibold text-white">CONFIDENTIAL</span>{" "}
          information and only seen by Officers who have the necessary need to know access in accordance with the Prospect
          and Member By-Laws, §6.8 in it's entirety.
        </p>
        <p className="text-red-400 text-xs">* Indicates required question</p>
      </SectionCard>

      <form onSubmit={handleSubmit} className="space-y-4">
        <SectionCard>
          <Label className="text-white">Handle</Label>
          <Input value={prefill.handle || ""} readOnly disabled className="text-white bg-slate-900 border-slate-600" data-testid="questionnaire-handle" />
          {prefill.chapter && <p className="text-xs text-slate-400">Chapter: {prefill.chapter}</p>}
        </SectionCard>

        <SectionCard>
          <Label className="text-white">Legal First &amp; Last Name <span className="text-red-400">*</span></Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your answer" required className="text-white" data-testid="questionnaire-name" />
        </SectionCard>

        <SectionCard>
          <Label className="text-white">Personal e-Mail Address <span className="text-red-400">*</span></Label>
          <Input type="email" value={form.personal_email} onChange={(e) => set("personal_email", e.target.value)} placeholder="Your answer" required className="text-white" data-testid="questionnaire-email" />
        </SectionCard>

        <SectionCard>
          <Label className="text-white">Phone Number <span className="text-red-400">*</span></Label>
          <p className="text-xs text-slate-400">Format as (xxx) xxx-xxxx</p>
          <Input value={form.phone} onChange={(e) => set("phone", formatPhone(e.target.value))} placeholder="(555) 555-5555" required className="text-white" data-testid="questionnaire-phone" />
        </SectionCard>

        <SectionCard>
          <Label className="text-white">Home Mailing Address (Address, City, State, Zip) <span className="text-red-400">*</span></Label>
          <Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Your answer" required className="text-white" data-testid="questionnaire-address" />
        </SectionCard>

        <SectionCard>
          <Label className="text-white">Birthdate <span className="text-red-400">*</span></Label>
          <p className="text-xs text-slate-400">Format mm/dd/yyyy</p>
          <Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} required className="text-white" data-testid="questionnaire-dob" />
        </SectionCard>

        <SectionCard>
          <Label className="text-white">Trucking Experience Start <span className="text-red-400">*</span></Label>
          <p className="text-xs text-slate-400">Month and year you started trucking — format MM/YYYY</p>
          <Input
            value={form.experience_start}
            onChange={(e) => set("experience_start", formatMonthYear(e.target.value, form.experience_start))}
            placeholder="MM/YYYY"
            required
            className="text-white"
            data-testid="questionnaire-experience-start"
          />
        </SectionCard>

        <SectionCard>
          <Label className="text-white">Are you a Military Veteran? <span className="text-red-400">*</span></Label>
          <YesNo value={form.military_service} onChange={(v) => set("military_service", v)} testId="questionnaire-military" />
          {form.military_service && (
            <div>
              <Label className="text-slate-300 text-sm">If you were military, which branch?</Label>
              <Select value={form.military_branch} onValueChange={(v) => set("military_branch", v)}>
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white mt-1" data-testid="questionnaire-branch-trigger">
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  {MILITARY_BRANCHES.map((b) => (
                    <SelectItem key={b} value={b} className="text-white hover:bg-slate-700">{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </SectionCard>

        <SectionCard>
          <Label className="text-white">Have you ever served as Police, Fire, or EMS? <span className="text-red-400">*</span></Label>
          <YesNo value={form.is_first_responder} onChange={(v) => set("is_first_responder", v)} testId="questionnaire-responder" />
        </SectionCard>

        <SectionCard>
          <Label className="text-white">Emergency Contact Name (Optional)</Label>
          <Input value={form.emergency_contact_name} onChange={(e) => set("emergency_contact_name", e.target.value)} placeholder="Your answer" className="text-white" data-testid="questionnaire-ec-name" />
          <Label className="text-white pt-2 block">Emergency Contact Phone Number &amp; Relationship (Optional)</Label>
          <Input value={form.emergency_contact_phone} onChange={(e) => set("emergency_contact_phone", formatPhone(e.target.value))} placeholder="(555) 555-5555" className="text-white" data-testid="questionnaire-ec-phone" />
          <Input value={form.emergency_contact_relationship} onChange={(e) => set("emergency_contact_relationship", e.target.value)} placeholder="Relationship (e.g. Spouse, Parent)" className="text-white" data-testid="questionnaire-ec-relationship" />
          <div className="flex items-center space-x-2 pt-1">
            <Checkbox
              id="ec_private"
              checked={form.emergency_contact_private}
              onCheckedChange={(checked) => set("emergency_contact_private", !!checked)}
              className="border-slate-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
              data-testid="questionnaire-ec-private"
            />
            <label htmlFor="ec_private" className="text-sm cursor-pointer text-slate-300">
              Keep my emergency contact private (visible only to my chapter officers and National)
            </label>
          </div>
        </SectionCard>

        <SectionCard>
          <Label className="text-white leading-relaxed">
            If you are voted to become a full standing member, do you consent to your Government Name, Handle, E-Mail
            Address and Phone Number being released in the Member Directory for other Members to access in accordance
            with the Member Bylaws, §6.8.3? <span className="text-red-400">*</span>
          </Label>
          <RadioGroup value={form.directory_consent} onValueChange={(v) => set("directory_consent", v)} className="space-y-2 pt-1" data-testid="questionnaire-consent">
            {[
              { value: "email_only", label: "E-Mail Address Only" },
              { value: "phone_only", label: "Phone Number Only" },
              { value: "both", label: "Both E-Mail Address & Phone Number" },
              { value: "none", label: "No, I do NOT consent to any information being released" },
            ].map((opt) => (
              <div key={opt.value} className="flex items-center space-x-2">
                <RadioGroupItem value={opt.value} id={`consent-${opt.value}`} className="border-slate-400 text-blue-500" data-testid={`questionnaire-consent-${opt.value}`} />
                <label htmlFor={`consent-${opt.value}`} className="text-sm text-slate-200 cursor-pointer">{opt.label}</label>
              </div>
            ))}
          </RadioGroup>
        </SectionCard>

        <div className="flex items-center justify-between pb-8">
          <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white px-8" data-testid="questionnaire-submit-button">
            {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>) : "Submit"}
          </Button>
          <span className="inline-flex items-center gap-1 text-xs text-green-400">
            <ShieldCheck className="w-3.5 h-3.5" /> 256-bit Encrypted
          </span>
        </div>
      </form>
    </Wrapper>
  );
}
